// The coach's LED strip and next-station screen (docs/plans/2026-10-18-ride-led-and-screen.md). What they say is a pure
// function of where the train is, so the solo ride and the shared ride (which reads the server's train) show the same
// thing, and it is tested without a browser. The words all come from voice.json (the announcement script) and the
// route's own station names: nothing new, nothing baked into a sprite.
//
// The display is the reliable channel and the voice the optional one: the announcer drops a line when another is
// speaking, the strip never does.
import VOICE from './voice.json';

/** data/story/s1e1.announce.json `led`: Hindi above in amber, English below in green, a hold, then a scroll. */
export const LED = { amber: '#FFB547', green: '#5BE37A', holdS: 1.2, pxPerS: 70, rows: 16, pitch: 2, fontPx: 13 };

type Stn = { name: string; hi: string; atM: number };
type Said = { hi: string; en: string };
const CLIPS = (VOICE as { clips: Record<string, Partial<Said>> }).clips;

export type LedPhase = { kind: 'moving'; next: Stn } | { kind: 'dwell'; at: Stn } | { kind: 'dock' };

/**
 * Where the train is, for the displays. `at` is the station whose doors are open (-1 while moving) and `docked` is the
 * ride's own end. The solo ride passes its dwell; the shared ride passes trainPos's `stop`, `done` and `next` (so a
 * train still running in to its first stop names that stop, not the one after).
 */
export function ledPhase(st: Stn[], d: number, at: number, docked: boolean, next = -1): LedPhase {
  if (docked) return { kind: 'dock' };
  if (at >= 0 && st[at]) return { kind: 'dwell', at: st[at] };
  return { kind: 'moving', next: st[next] ?? st.find((s) => s.atM > d + 1) ?? st[st.length - 1] };
}

/** A clip's first sentence in each script: the strip carries "Doors will open on the left.", not the whole line. */
const first = (s: string) => (s.match(/^.*?[.।](?=\s|$)/)?.[0] ?? s).trim();

/** The two lines and a key that changes whenever either does (so the scroll restarts and the hold applies again). */
export function ledLines(p: LedPhase, disrupt: string | null = null): Said & { key: string } {
  let hi: string, en: string;
  if (p.kind === 'dock') { const c = CLIPS['pa-dock-left']; hi = first(c.hi!); en = first(c.en!); }
  else if (p.kind === 'dwell') { hi = p.at.hi; en = p.at.name; }
  else if (disrupt && CLIPS[disrupt]?.en) { hi = CLIPS[disrupt].hi ?? ''; en = CLIPS[disrupt].en!; }
  // the approach clip's sentence with the route's own name: a clip may spell a name for the voice ("G T B Nagar")
  else { hi = `अगला स्टेशन ${p.next.hi} है।`; en = `The next station is ${p.next.name}.`; }
  return { hi, en, key: `${hi}|${en}` };
}

/**
 * The x offset of a line `t` seconds after its text changed, in px: still for the hold, then leftwards at pxPerS until
 * it has wholly left, in again from the right edge and back to the start, where it holds again. A line that fits stays.
 */
export function scrollX(t: number, textW: number, stripW: number, holdS = LED.holdS, v = LED.pxPerS): number {
  if (textW <= stripW || t <= holdS) return 0;
  const run = (textW + stripW) / v, u = ((t - holdS) % (holdS + run));
  if (u >= run) return 0; // holding again at the start
  const x = u * v;
  return x <= textW ? -x : stripW - (x - textW);
}

/**
 * `?disrupt=1` (the closed-station line) or `?disrupt=<clip id>`, else the route's own `disrupt`. Off by default: the
 * June episode has no disruption, and the rumour's check is that the display keeps saying Patel Chowk.
 */
export function disruptFrom(search: string, route?: string): string | null {
  const q = new URLSearchParams(search).get('disrupt');
  const id = q === '1' ? 'pa-closed' : q;
  for (const c of [id, route]) if (c && c.startsWith('pa-') && CLIPS[c]?.en) return c;
  return null;
}

/**
 * The screen: the station it is about (the one ahead, or the one whose doors are open), the stops after it up to the
 * ride's end (the next three, and how many past those), or, in a disruption, the line alone with no artwork.
 */
export function screenOf(st: Stn[], p: LedPhase, endM: number, disrupt: string | null = null) {
  if (p.kind === 'moving' && disrupt && CLIPS[disrupt]?.en) return { disrupt: { hi: CLIPS[disrupt].hi ?? '', en: CLIPS[disrupt].en! }, station: null, ahead: [] as Stn[], more: 0 };
  const s = p.kind === 'moving' ? p.next : p.kind === 'dwell' ? p.at : st.find((x) => Math.abs(x.atM - endM) < 1) ?? st[st.length - 1];
  const rest = st.filter((x) => x.atM > s.atM && x.atM <= endM + 1);
  return { disrupt: null, station: s, ahead: rest.slice(0, 3), more: Math.max(0, rest.length - 3) };
}

// ------------------------------------------------------------------ raster (browser only)

/** A line of text as LEDs: `cols` wide, LED.rows tall, 1 where the glyph covers the cell. */
export function rasterise(text: string, font = 'Mukta,system-ui,sans-serif'): { cols: number; bits: Uint8Array } {
  const c = document.createElement('canvas'), g = c.getContext('2d')!, f = `600 ${LED.fontPx}px ${font}`;
  g.font = f; const cols = Math.ceil(g.measureText(text).width) + 2;
  c.width = cols; c.height = LED.rows;
  g.font = f; g.fillStyle = '#fff'; g.fillText(text, 1, Math.round(LED.rows * 0.72)); // room above for matras, below for ु
  const d = g.getImageData(0, 0, cols, LED.rows).data, bits = new Uint8Array(cols * LED.rows);
  for (let i = 0; i < bits.length; i++) bits[i] = d[i * 4 + 3] > 115 ? 1 : 0;
  return { cols, bits };
}

/** The lit dots of a rasterised line, drawn once onto a canvas at the pitch; the strip blits a window of it a frame. */
export function dots(r: { cols: number; bits: Uint8Array }, colour: string, pitch = LED.pitch): HTMLCanvasElement {
  const c = document.createElement('canvas'), g = c.getContext('2d')!;
  c.width = r.cols * pitch; c.height = LED.rows * pitch; g.fillStyle = colour;
  for (let j = 0; j < LED.rows; j++) for (let i = 0; i < r.cols; i++) if (r.bits[j * r.cols + i]) {
    g.beginPath(); g.arc(i * pitch + pitch / 2, j * pitch + pitch / 2, pitch * 0.42, 0, Math.PI * 2); g.fill();
  }
  return c;
}

/** The unlit grid of a strip `cols` x `rows` LEDs: the faint dots that make it read as a strip when nothing is lit. */
export function grid(cols: number, rows: number, pitch = LED.pitch): HTMLCanvasElement {
  const c = document.createElement('canvas'), g = c.getContext('2d')!;
  c.width = cols * pitch; c.height = rows * pitch; g.fillStyle = 'rgba(255,255,255,.08)';
  for (let j = 0; j < rows; j++) for (let i = 0; i < cols; i++) { g.beginPath(); g.arc(i * pitch + pitch / 2, j * pitch + pitch / 2, pitch * 0.42, 0, Math.PI * 2); g.fill(); }
  return c;
}
